import { PUBLIC_ENVIRONMENT } from '$env/static/public';
import posthogClient from 'posthog-js';
import type { CaptureOptions, EventName, Properties } from 'posthog-js';
import { IS_AQUEDUCT } from '$lib/site';

const DEV_POSTHOG_ID: string = 'phc_lajN6lUlP8U4PSCo3YqyXIzU42ccUSc91QHamXvKlHY';
const PROD_POSTHOG_ID: string = 'phc_p7EDVq5HcrSd2qzxX8rcNj7kbAO2q5k1E1YcQptdA72';

let allowed: boolean = false;
let initialized: boolean = false;
let pendingIdentity:
	| {
			distinctId: string;
			properties?: Properties;
			propertiesSetOnce?: Properties;
	  }
	| undefined;
let pendingPersonProperties: Properties | undefined;
let pendingPersonPropertiesSetOnce: Properties | undefined;

function getPosthogId(): string | undefined {
	if (PUBLIC_ENVIRONMENT === 'prod') return PROD_POSTHOG_ID;
	if (PUBLIC_ENVIRONMENT === 'local' || PUBLIC_ENVIRONMENT === 'dev') return DEV_POSTHOG_ID;
	return undefined;
}

function applyPendingIdentity(): void {
	if (!initialized || !allowed) return;

	if (pendingIdentity) {
		posthogClient.identify(
			pendingIdentity.distinctId,
			pendingIdentity.properties,
			pendingIdentity.propertiesSetOnce
		);
	}
	if (pendingPersonProperties || pendingPersonPropertiesSetOnce) {
		posthogClient.setPersonProperties(pendingPersonProperties, pendingPersonPropertiesSetOnce);
	}
}

export function enableAnalytics(): void {
	allowed = true;

	if (initialized) {
		posthogClient.opt_in_capturing({ captureEventName: false });
		if (!IS_AQUEDUCT) posthogClient.startSessionRecording();
		applyPendingIdentity();
		return;
	}

	const posthogId: string | undefined = getPosthogId();
	if (!posthogId) {
		console.error('Analytics not initialized due to invalid environment.');
		return;
	}

	posthogClient.init(posthogId, {
		api_host: 'https://posthog.iliad.ai',
		opt_out_persistence_by_default: true,
		disable_session_recording: IS_AQUEDUCT,
		session_recording: { maskAllInputs: false }
	});
	initialized = true;
	posthogClient.opt_in_capturing({ captureEventName: false });
	applyPendingIdentity();
}

export function disableAnalytics(): void {
	allowed = false;
	if (!initialized) return;

	posthogClient.stopSessionRecording();
	posthogClient.reset();
	posthogClient.opt_out_capturing();
}

export const analytics = {
	capture(eventName: EventName, properties?: Properties | null, options?: CaptureOptions): void {
		if (!initialized || !allowed) return;
		posthogClient.capture(eventName, properties, options);
	},

	identify(distinctId: string, properties?: Properties, propertiesSetOnce?: Properties): void {
		pendingIdentity = { distinctId, properties, propertiesSetOnce };
		if (!initialized || !allowed) return;
		posthogClient.identify(distinctId, properties, propertiesSetOnce);
	},

	setPersonProperties(properties?: Properties, propertiesSetOnce?: Properties): void {
		pendingPersonProperties = {
			...pendingPersonProperties,
			...properties
		};
		pendingPersonPropertiesSetOnce = {
			...pendingPersonPropertiesSetOnce,
			...propertiesSetOnce
		};
		if (!initialized || !allowed) return;
		posthogClient.setPersonProperties(properties, propertiesSetOnce);
	},

	reset(resetDeviceId?: boolean): void {
		pendingIdentity = undefined;
		pendingPersonProperties = undefined;
		pendingPersonPropertiesSetOnce = undefined;
		if (!initialized) return;
		posthogClient.reset(resetDeviceId);
		if (!allowed) posthogClient.opt_out_capturing();
	}
};
